import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const GOLD = '#D4AF37';
const BG_CARD = '#0d1117';
const BORDER = 'rgba(255,255,255,0.07)';
const TEXT_SEC = '#6b7280';
const GREEN = '#10b981';
const RED = '#ef4444';
const BLUE = '#3b82f6';
const ORANGE = '#f59e0b';

const STATUS_COLORS = {
  pending: ORANGE, confirmed: BLUE, completed: GREEN,
  cancelled: '#6b7280', declined: RED, no_show: RED,
};
const STATUS_LABELS = {
  pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed',
  cancelled: 'Cancelled', declined: 'Declined', no_show: 'No Show',
};

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function Card({ children, style = {} }) {
  return (
    <div style={{
      background: BG_CARD,
      border: `1px solid ${BORDER}`,
      borderRadius: 16,
      padding: '20px',
      ...style,
    }}>
      {children}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/clients/${id}`).then(r => {
      setClient(r.data);
      setForm({ name: r.data.name, phone: r.data.phone || '', email: r.data.email || '', notes: r.data.notes || '' });
      if (r.data.referral_code) {
        setReferralCode(r.data.referral_code);
      } else {
        api.get(`/clients/${id}/referral-code`).then(rc => setReferralCode(rc.data.referral_code)).catch(() => {});
      }
    }).finally(() => setLoading(false));
    // Load referral count
    api.get(`/referrals/client/${id}`).then(r => setReferralCount(r.data.length)).catch(() => {});
  }, [id]);

  const handleCopyLink = () => {
    const link = `joinmane.com/barber?ref=${referralCode}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put(`/clients/${id}`, form);
      setClient(c => ({ ...c, ...data }));
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <AdminLayout>
      <div style={{ textAlign: 'center', padding: 80 }}>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    </AdminLayout>
  );
  if (!client) return <AdminLayout><p style={{ color: TEXT_SEC }}>Client not found</p></AdminLayout>;

  const initials = client.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const totalSpent = (client.appointments || []).filter(a => a.status === 'completed').reduce((s, a) => s + parseFloat(a.price || 0), 0);
  const visitCount = (client.appointments || []).filter(a => a.status === 'completed').length;

  return (
    <AdminLayout>
      {/* Back */}
      <div style={{ marginBottom: 20 }}>
        <Link to="/dash/clients" style={{ color: TEXT_SEC, fontSize: 13, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Clients
        </Link>
      </div>

      {/* Profile hero */}
      <Card style={{ marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, ${GOLD}60, ${GOLD})`,
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%', flexShrink: 0,
            background: 'rgba(212,175,55,0.1)',
            border: '2px solid rgba(212,175,55,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 20, fontWeight: 800, color: GOLD,
          }}>
            {initials}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#fff' }}>{client.name}</div>
            <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 2 }}>
              Client since {new Date(client.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Visits', value: visitCount, color: GOLD },
            { label: 'Total Spent', value: `$${totalSpent.toFixed(0)}`, color: GREEN },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`,
              borderRadius: 12, padding: '12px', textAlign: 'center',
            }}>
              <div style={{ color: s.color, fontWeight: 900, fontSize: 20 }}>{s.value}</div>
              <div style={{ color: TEXT_SEC, fontSize: 11, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Contact info */}
        {!editing ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {[
                { icon: null, label: 'Phone', value: client.phone },
                { icon: null, label: 'Email', value: client.email },
              ].map(({ icon, label, value }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 11, fontWeight: 700, color: TEXT_SEC, flexShrink: 0,
                  }}>{label === 'Phone' ? 'PH' : '@'}</div>
                  <div>
                    <div style={{ color: TEXT_SEC, fontSize: 10, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>{label}</div>
                    <div style={{ color: value ? '#fff' : TEXT_SEC, fontSize: 14, marginTop: 1 }}>
                      {value || 'Not provided'}
                    </div>
                  </div>
                </div>
              ))}
              {client.notes && (
                <div style={{
                  background: 'rgba(212,175,55,0.06)', border: `1px solid rgba(212,175,55,0.2)`,
                  borderRadius: 10, padding: '10px 14px', marginTop: 4,
                }}>
                  <div style={{ color: TEXT_SEC, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Notes</div>
                  <div style={{ color: '#d1d5db', fontSize: 13 }}>{client.notes}</div>
                </div>
              )}
            </div>
            <button onClick={() => setEditing(true)} style={{
              width: '100%', padding: '12px', borderRadius: 10,
              background: 'transparent', border: `1px solid ${BORDER}`,
              color: '#9ca3af', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}>
              Edit Profile
            </button>
          </>
        ) : (
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              { key: 'name', label: 'Name' },
              { key: 'phone', label: 'Phone' },
              { key: 'email', label: 'Email' },
            ].map(({ key, label }) => (
              <div key={key}>
                <div style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{label}</div>
                <input
                  className="input"
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  required={key === 'name'}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>
            ))}
            <div>
              <div style={{ color: TEXT_SEC, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Notes</div>
              <textarea
                className="input"
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3}
                style={{ resize: 'none', width: '100%', boxSizing: 'border-box' }}
                placeholder="e.g. prefers low fade"
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" disabled={saving} style={{
                flex: 1, padding: '12px', borderRadius: 10,
                background: `linear-gradient(135deg, ${GOLD}, #f5c842)`,
                border: 'none', color: '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>{saving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={() => setEditing(false)} style={{
                padding: '12px 16px', borderRadius: 10,
                background: 'transparent', border: `1px solid ${BORDER}`,
                color: TEXT_SEC, fontSize: 13, cursor: 'pointer',
              }}>Cancel</button>
            </div>
          </form>
        )}
      </Card>

      {/* Share & Earn */}
      {referralCode && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 4 }}>Share & Earn</div>
          <div style={{ color: TEXT_SEC, fontSize: 12, marginBottom: 16 }}>
            Share this booking link. Every new client they refer is tracked automatically.
            {referralCount > 0 && <span style={{ color: GOLD, fontWeight: 700, marginLeft: 6 }}>✓ {referralCount} referral{referralCount !== 1 ? 's' : ''} so far</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              flex: 1, background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)',
              borderRadius: 10, padding: '10px 14px', fontSize: 13, color: GOLD,
              fontFamily: 'monospace', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
            }}>
              joinmane.com/barber?ref={referralCode}
            </div>
            <button
              onClick={handleCopyLink}
              style={{
                flexShrink: 0, padding: '10px 16px', borderRadius: 10,
                background: copied ? 'rgba(16,185,129,0.15)' : `linear-gradient(135deg, ${GOLD}, #f5c842)`,
                border: copied ? '1px solid #10b981' : 'none',
                color: copied ? '#10b981' : '#000', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </Card>
      )}

      {/* Appointment history */}
      <Card>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 16 }}>Appointment History</div>
        {!client.appointments || client.appointments.length === 0 ? (
          <div style={{ textAlign: 'center', color: TEXT_SEC, padding: '32px 0', fontSize: 13 }}>
            No appointments yet
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {client.appointments.map(a => {
              const sc = STATUS_COLORS[a.status] || '#6b7280';
              return (
                <div key={a.id} style={{
                  background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`,
                  borderRadius: 12, padding: '12px 14px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{a.service_name}</div>
                    <div style={{ color: TEXT_SEC, fontSize: 12, marginTop: 2 }}>
                      {new Date(a.appointment_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      {a.start_time ? ` · ${formatTime(a.start_time)}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div style={{ color: GOLD, fontWeight: 700, fontSize: 14 }}>${parseFloat(a.price || 0).toFixed(0)}</div>
                    <div style={{
                      fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '0.05em', color: sc,
                      background: `${sc}15`, border: `1px solid ${sc}30`,
                      borderRadius: 6, padding: '2px 8px',
                    }}>
                      {STATUS_LABELS[a.status]}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </AdminLayout>
  );
}
